import { CircularProgress, TextField } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AnswerTypes, OptionKinds, OptionValue, QnA } from '../../../actions/defectAssistant/types'
import { colors } from '../../../Theme'
import { AiButton } from '../CtaButtons'
import { CheckboxOptionList, MatchedDefectInfo, optionKey, renderOptions } from './shared'
import {
  AI_FIELD_SX,
  OptionDesc,
  OptionsList,
  OptionTitle,
  ScrollOptionButton,
  Section,
  SectionHeader,
  SectionQuestion,
  SectionReasoning,
  Spacer,
} from './styles'

export const ScrollMultiSelect = ({
  question,
  loading,
  onSubmit,
}: {
  question: QnA
  loading: boolean
  onSubmit: (answers: OptionValue[]) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const answerOptions = question.options.filter((opt) => opt.kind === OptionKinds.ANSWER)
  const freeTextOption = question.options.find((opt) => opt.kind === OptionKinds.FREE_TEXT)
  const nothingToAddOption = question.options.find((opt) => opt.kind === OptionKinds.NOTHING_TO_ADD)
  const [selected, setSelected] = useState<Set<number>>(() => {
    const prev = question.answers
    if (!prev?.length) return new Set()
    const prevKeys = new Set(prev.map(optionKey))
    const result = new Set<number>()
    answerOptions.forEach((opt, idx) => {
      if (prevKeys.has(optionKey(opt))) result.add(idx)
    })
    return result
  })
  const [freeText, setFreeText] = useState<string>(() => {
    const freeAnswer = (question.answers ?? []).find((a) => a.kind === OptionKinds.FREE_TEXT)
    return freeAnswer?.label ?? ''
  })

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const handleSubmit = () => {
    if (loading) return
    const picked: OptionValue[] = answerOptions.filter((_, idx) => selected.has(idx))
    const trimmed = freeText.trim()
    if (freeTextOption && trimmed.length > 0) picked.push({ ...freeTextOption, label: trimmed })
    if (picked.length === 0 && nothingToAddOption) picked.push(nothingToAddOption)
    onSubmit(picked)
  }

  return (
    <Section>
      <SectionQuestion>{question.question}</SectionQuestion>
      <CheckboxOptionList
        uuid={question.uuid}
        options={answerOptions}
        isChecked={(_opt, idx) => selected.has(idx)}
        onToggle={toggle}
      />
      {freeTextOption && (
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={
            freeTextOption.label ?? t('defect.aiAssistant.completenessFreeTextPlaceholder')
          }
          sx={AI_FIELD_SX}
        />
      )}
      <AiButton type="button" disabled={loading} onClick={handleSubmit}>
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ color: 'white' }} />
            {t('defect.aiAssistant.submitAnswerLoading')}
          </>
        ) : (
          t('defect.aiAssistant.submitAnswer')
        )}
      </AiButton>
    </Section>
  )
}

export const ScrollDuplicateCheck = ({
  question,
  loading,
  amendLoading,
  showCommentFieldInitially,
  suggestedComment,
  suggestedCommentLoading,
  onSubmit,
  onAmendComment,
  onAbortRequest,
}: {
  question: QnA
  loading: boolean
  amendLoading: boolean
  showCommentFieldInitially: boolean
  // The proposed comment once it is known. Streamed separately from the verdict, so it can be null
  // for a while after this section is already on screen.
  suggestedComment: string | null
  suggestedCommentLoading: boolean
  onSubmit: (answers: OptionValue[]) => void
  onAmendComment: (defectId: string, comment: string) => void
  onAbortRequest: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const amendOption = question.options.find(
    (opt) => opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT
  )
  const createOption = question.options.find((opt) => opt.kind === OptionKinds.CREATE_NEW_DEFECT)
  const matched = question.matchedDefect
  const [ownComment, setOwnComment] = useState<string | null>(null)
  const amendComment = ownComment ?? suggestedComment ?? ''
  const [showCommentField, setShowCommentField] = useState(showCommentFieldInitially)

  const deEmphasised = showCommentField

  return (
    <Section>
      <SectionQuestion>{t('defect.aiAssistant.duplicate.foundDefectHeader')}</SectionQuestion>
      {matched && <MatchedDefectInfo matched={matched} />}
      <Spacer />
      <SectionQuestion>{t('defect.aiAssistant.duplicate.actionQuestion')}</SectionQuestion>
      <OptionsList>
        {amendOption && (
          <ScrollOptionButton
            type="button"
            optionState={showCommentField ? 'selected' : 'active'}
            onClick={() => !showCommentField && setShowCommentField(true)}
          >
            <OptionTitle>{amendOption.label ?? ''}</OptionTitle>
            {question.summary && <OptionDesc>{question.summary}</OptionDesc>}
          </ScrollOptionButton>
        )}
        {createOption && (
          <ScrollOptionButton
            type="button"
            optionState={deEmphasised ? 'secondary' : 'active'}
            onClick={() => !loading && onSubmit([createOption])}
          >
            <OptionTitle dimmed={deEmphasised}>{createOption.label ?? ''}</OptionTitle>
            <OptionDesc dimmed={deEmphasised}>
              {t('defect.aiAssistant.duplicate.createNewHint')}
            </OptionDesc>
          </ScrollOptionButton>
        )}
        <ScrollOptionButton
          type="button"
          optionState={deEmphasised ? 'secondary' : 'active'}
          onClick={onAbortRequest}
        >
          <OptionTitle dimmed={deEmphasised}>
            {t('defect.aiAssistant.duplicate.abortOption')}
          </OptionTitle>
          <OptionDesc dimmed={deEmphasised}>
            {t('defect.aiAssistant.duplicate.abortOptionHint')}
          </OptionDesc>
        </ScrollOptionButton>
      </OptionsList>
      {showCommentField && amendOption && (
        <>
          <SectionQuestion>{t('defect.aiAssistant.duplicate.commentTitle')}</SectionQuestion>
          <TextField
            multiline
            minRows={2}
            fullWidth
            value={amendComment}
            onChange={(e) => setOwnComment(e.target.value)}
            placeholder={
              suggestedCommentLoading
                ? t('defect.aiAssistant.duplicate.commentLoading')
                : t('defect.aiAssistant.duplicate.commentPlaceholder')
            }
            sx={AI_FIELD_SX}
          />
          <AiButton
            type="button"
            disabled={amendLoading || amendComment.trim().length === 0}
            onClick={() => {
              const defectId = amendOption.value != null ? String(amendOption.value) : ''
              if (defectId) onAmendComment(defectId, amendComment)
            }}
          >
            {amendLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                {t('defect.aiAssistant.duplicate.addCommentLoading')}
              </>
            ) : (
              t('defect.aiAssistant.duplicate.addCommentButton', { defectId: matched?.id ?? '' })
            )}
          </AiButton>
        </>
      )}
    </Section>
  )
}

// Locked (answered) duplicate-check summary. Clicking an option re-opens the question.
export const LockedDuplicateCheck = ({
  qna,
  onReOpen,
  onAbort,
}: {
  qna: QnA
  onReOpen: (opt: OptionValue) => void
  onAbort: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const selectedValues = new Set((qna.answers ?? []).map(optionKey))
  const isCreateNew = (qna.answers ?? [])[0]?.kind === OptionKinds.CREATE_NEW_DEFECT
  return (
    <Section>
      <SectionQuestion muted>{t('defect.aiAssistant.duplicate.foundDefectHeader')}</SectionQuestion>
      {qna.matchedDefect && <MatchedDefectInfo matched={qna.matchedDefect} dimmed={isCreateNew} />}
      <Spacer />
      <SectionQuestion muted>{t('defect.aiAssistant.duplicate.actionQuestion')}</SectionQuestion>
      <OptionsList>
        {qna.options
          .filter(
            (o) =>
              o.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT ||
              o.kind === OptionKinds.CREATE_NEW_DEFECT
          )
          .map((opt) => {
            const isSelected = selectedValues.has(optionKey(opt))
            const hint =
              opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT
                ? qna.summary ?? undefined
                : t('defect.aiAssistant.duplicate.createNewHint')
            return (
              <ScrollOptionButton
                key={optionKey(opt)}
                type="button"
                optionState={isSelected ? 'chosen' : 'secondary'}
                onClick={() => onReOpen(opt)}
              >
                <OptionTitle dimmed={!isSelected}>{opt.label ?? ''}</OptionTitle>
                {hint && <OptionDesc dimmed={!isSelected}>{hint}</OptionDesc>}
              </ScrollOptionButton>
            )
          })}
        <ScrollOptionButton type="button" optionState="secondary" onClick={onAbort}>
          <OptionTitle dimmed>{t('defect.aiAssistant.duplicate.abortOption')}</OptionTitle>
          <OptionDesc dimmed>{t('defect.aiAssistant.duplicate.abortOptionHint')}</OptionDesc>
        </ScrollOptionButton>
      </OptionsList>
    </Section>
  )
}

// Locked (answered) question. The whole section is clickable (and keyboard-activatable) to re-open it.
export const LockedQuestion = ({
  qna,
  onReOpen,
}: {
  qna: QnA
  onReOpen: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const isMulti = qna.answerType === AnswerTypes.MULTISELECT
  const selectedValues = new Set((qna.answers ?? []).map(optionKey))
  const answerOptions = qna.options.filter((o) => o.kind === OptionKinds.ANSWER)
  const freeTextOption = qna.options.find((o) => o.kind === OptionKinds.FREE_TEXT)
  const lockedFreeText =
    (qna.answers ?? []).find((a) => a.kind === OptionKinds.FREE_TEXT)?.label ?? ''

  return (
    <Section
      role="button"
      tabIndex={0}
      onClick={onReOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onReOpen()
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <SectionHeader>
        <SectionQuestion muted>{qna.question}</SectionQuestion>
      </SectionHeader>
      {/* Multiselect keeps its checkbox appearance when locked (disabled), so it doesn't morph into
          button-like elements after confirming. */}
      {isMulti ? (
        <>
          <CheckboxOptionList
            uuid={qna.uuid}
            options={answerOptions}
            isChecked={(opt) => selectedValues.has(optionKey(opt))}
          />
          {freeTextOption && (
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={lockedFreeText}
              // Read-only rather than disabled: browsers swallow click events on disabled inputs, so
              // a disabled field would block the Section's onReOpen. Read-only keeps the text shown
              // normally (#333) and lets the click bubble up to re-open the question for editing.
              InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
              placeholder={
                freeTextOption.label ?? t('defect.aiAssistant.completenessFreeTextPlaceholder')
              }
              sx={AI_FIELD_SX}
            />
          )}
        </>
      ) : (
        renderOptions(qna.options, (opt) =>
          selectedValues.has(optionKey(opt)) ? 'selected' : 'dimmed'
        )
      )}
    </Section>
  )
}

// Active single-select question — full interaction.
export const ActiveSingleSelect = ({
  qna,
  loading,
  onPick,
}: {
  qna: QnA
  loading: boolean
  onPick: (opt: OptionValue) => void
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Section>
      <SectionQuestion>{qna.question}</SectionQuestion>
      {renderOptions(qna.options, () => 'active', onPick)}
      {qna.options.length === 0 && (
        <SectionReasoning>{t('defect.aiAssistant.noOptions')}</SectionReasoning>
      )}
      {loading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
    </Section>
  )
}

// Placeholder for a step the assistant plans to ask but hasn't returned yet. `label` is display text
// straight from the backend, not a translation key — the flow is server-owned, and so are its titles.
export const PlaceholderSection = ({
  label,
  firstLoading,
}: {
  label: string
  firstLoading: boolean
}): JSX.Element => (
  <Section style={{ opacity: 0.25 }}>
    <SectionQuestion muted>{label}</SectionQuestion>
    {firstLoading ? (
      <CircularProgress size={20} sx={{ color: colors.purple20, alignSelf: 'center' }} />
    ) : (
      <SectionReasoning>…</SectionReasoning>
    )}
  </Section>
)

// A question that's been returned but isn't yet reachable.
export const FutureSection = ({ qna }: { qna: QnA }): JSX.Element => (
  <Section style={{ opacity: 0.25 }}>
    <SectionQuestion muted>{qna.question}</SectionQuestion>
    {renderOptions(qna.options, () => 'dimmed')}
    {qna.options.length === 0 && <SectionReasoning>…</SectionReasoning>}
  </Section>
)
